import type { KnownPairSummary, PrimerEstimate, PrimerSideEstimate } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SequenceRead } from '../../Util/orderSettingDetection/sequenceHead.ts'
import { readSequenceHead } from '../../Util/orderSettingDetection/sequenceHead.ts'
import { bestPrefixEditDistance, cleanPrimer, toMasks } from '../../Util/orderSettingDetection/iupac.ts'
import type { KnownPrimerPair } from '../../Util/orderSettingDetection/primerRegistry.ts'
import { stripAdapter } from '../../Util/orderSettingDetection/primerRegistry.ts'
import type { TaggedPrimerSide } from '../../Util/orderSettingDetection/taggedPrimerEstimation.ts'
import { estimateTaggedPrimers, genePrimersOf } from '../../Util/orderSettingDetection/taggedPrimerEstimation.ts'

/** Must stay free of DOM, store and path-alias imports: it runs inside the detection worker. */

export type EstimateGroup = { key: string; forward: Blob; reverse?: Blob }

export type EstimateRequest = {
  pair: KnownPairSummary
  groups: EstimateGroup[]
  /** The organisation's earlier primer entries, each with how many samples carried it. */
  history: { primer: string; weight: number }[]
}

export type EstimateResult = { estimates: { key: string; estimate: PrimerEstimate }[] }

const FAILED_REASON = 'estimation failed'

const failedSide: PrimerSideEstimate = {
  primer: null,
  prefix: null,
  gene: null,
  spelledByLab: false,
  support: 0,
  confidence: 'none',
  reason: FAILED_REASON
}

/** Stands in for an estimate that failed or timed out: no estimate is coming, so the primers are asked for. */
export const FAILED_ESTIMATE: PrimerEstimate = { forward: failedSide, reverse: failedSide }

export const isFailedEstimate = (estimate: PrimerEstimate): boolean => isFailedSide(estimate.forward)

export const isFailedSide = (side: PrimerSideEstimate): boolean => side.reason === FAILED_REASON

/** Fewer earlier orders are too thin to say how the lab writes a primer; the registry's spelling is used instead. */
export const MIN_HISTORY_ORDERS = 3

export type LabPrimerHistory = {
  orderCount: number
  entries?: { forwardPrimer: string; reversePrimer: string; samples: number }[]
} | null

/**
 * The organisation's own entries as evidence of how it writes primers, or none when they span fewer than
 * {@link MIN_HISTORY_ORDERS} orders. Never another organisation's entries.
 */
export const historyEntries = (history: LabPrimerHistory) =>
  history && history.orderCount >= MIN_HISTORY_ORDERS ? (history.entries ?? []) : []

export const spellingHistory = (history: LabPrimerHistory) =>
  historyEntries(history).flatMap((entry) => [
    { primer: entry.forwardPrimer, weight: entry.samples },
    { primer: entry.reversePrimer, weight: entry.samples }
  ])

const asKnownPair = (pair: KnownPairSummary): KnownPrimerPair => ({ id: 'wizard', ...pair })

const OPENER_READS = 60
const OPENER_MAX_START = 24
const OPENER_CORE = 18

/** Which side of the pair opens R1: the gene primer found at the 5′ start of more of its reads. */
export const r1OpensWithSecond = (r1: readonly SequenceRead[], pair: KnownPairSummary): boolean => {
  const core = (primer: string) => {
    const clean = stripAdapter(primer)
    const masks = toMasks(clean.slice(-Math.min(OPENER_CORE, clean.length)))
    return { masks, maxStart: OPENER_MAX_START + Math.max(0, clean.length - masks.length), cutoff: Math.floor(masks.length * 0.15) }
  }
  const first = core(pair.first)
  const second = core(pair.second)
  let firstHits = 0
  let secondHits = 0
  for (const read of r1.slice(0, OPENER_READS)) {
    if (bestPrefixEditDistance(first.masks, read.sequence, first.maxStart, first.cutoff) <= first.cutoff) {
      firstHits++
    }
    if (bestPrefixEditDistance(second.masks, read.sequence, second.maxStart, second.cutoff) <= second.cutoff) {
      secondHits++
    }
  }
  return secondHits > firstHits
}

/**
 * The registered spelling the lab itself wrote most often on its earlier orders, among those as long as
 * the one the reads picked (the reads cannot tell IUPAC-equal spellings apart). Measured on prod, this is
 * what lifts exact matches from about half of the samples to nine in ten. `variants` must be only the
 * spellings that fit the reads: history never overrules them.
 */
export const labSpelling = (variant: string, variants: readonly string[], history: EstimateRequest['history']): string | null => {
  const counts = new Map<string, number>()
  for (const candidate of variants.filter((v) => v.length === variant.length)) {
    for (const entry of history) {
      if (cleanPrimer(entry.primer).endsWith(candidate)) {
        counts.set(candidate, (counts.get(candidate) ?? 0) + entry.weight)
      }
    }
  }
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
}

const summarizeSide = (side: TaggedPrimerSide, history: EstimateRequest['history']): PrimerSideEstimate => {
  const spelled = side.variant ? labSpelling(side.variant, side.tiedVariants, history) : null
  const gene = spelled ?? side.variant
  const primer =
    side.primer !== null && side.variant !== null && gene !== null
      ? side.primer.slice(0, side.primer.length - side.variant.length) + gene
      : side.primer
  return {
    primer,
    prefix: side.prefix,
    gene,
    spelledByLab: spelled !== null,
    support: side.support,
    located: side.located,
    ...(side.spacer ? { spacer: true } : {}),
    confidence: side.confidence,
    reason: side.reason
  }
}

export const estimateGroup = (
  files: readonly (readonly SequenceRead[])[],
  pair: KnownPairSummary,
  history: EstimateRequest['history']
): PrimerEstimate => {
  const swapped = files.length > 1 && r1OpensWithSecond(files[0], pair)
  const genes = genePrimersOf(asKnownPair(pair), swapped)
  const estimate = estimateTaggedPrimers(files, genes)
  return {
    forward: summarizeSide(estimate.forward, history),
    reverse: summarizeSide(estimate.reverse, history)
  }
}

export const runEstimation = async (request: EstimateRequest): Promise<EstimateResult> => {
  const estimates: EstimateResult['estimates'] = []
  for (const group of request.groups) {
    const files = await Promise.all(
      [group.forward, group.reverse].filter((blob): blob is Blob => !!blob).map((blob) => readSequenceHead(blob))
    )
    estimates.push({ key: group.key, estimate: estimateGroup(files, request.pair, request.history) })
  }
  return { estimates }
}
